import { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { logout } from 'utils/logout'

import {
  buildDefaultCsvFilename,
  getCurrentMonthPeriod,
  getLastMonthPeriod,
  getTodayPeriod,
  getYesterdayPeriod,
  isBeforeStartDate,
  isDisabledDownload,
  isOverDownloadPeriod,
  periodFromDateStrings,
  toCreatedAtRange,
} from '../utils/csvDownloadPeriod'
import { getDownloadErrorMessage, isAuthDownloadError } from '../utils/downloadErrorHandling'
import { useDownloadShipmentsCsv } from './mutations/useDownloadShipmentsCsv'
import type { CsvEncodingType, DownloadPeriod } from '../type'
import { CSV_ENCODING_TYPE } from '../type'

type Args = {
  tenantId: number | null
}

export type DeliveryPerformanceCsvDownloadControllerReturn = {
  period: DownloadPeriod
  encodingType: CsvEncodingType
  isOverPeriod: boolean
  isBeforeStartDate: boolean
  isDisabled: boolean
  isDownloading: boolean
  isConfirmOpen: boolean
  downloadError: string | null
  setStartDate: (date: Date) => void
  setEndDate: (date: Date) => void
  setEncodingType: (encodingType: CsvEncodingType) => void
  handleYesterdayClick: () => void
  handleTodayClick: () => void
  handleLastMonthClick: () => void
  handleCurrentMonthClick: () => void
  openConfirm: () => void
  closeConfirm: () => void
  handleDownload: () => void
}

export const useDeliveryPerformanceCsvDownloadController = ({
  tenantId,
}: Args): DeliveryPerformanceCsvDownloadControllerReturn => {
  // 一覧の「CSV実績ダウンロード」導線が location.state で期間を引き継ぐ。無ければ当日にフォールバック。
  const { state } = useLocation<{ dateFrom?: string; dateTo?: string } | undefined>()
  const [period, setPeriod] = useState<DownloadPeriod>(
    () => periodFromDateStrings(state?.dateFrom, state?.dateTo) ?? getTodayPeriod()
  )
  const [encodingType, setEncodingType] = useState<CsvEncodingType>(CSV_ENCODING_TYPE.ShiftJis)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const { mutate, isPending } = useDownloadShipmentsCsv()

  const setStartDate = useCallback((date: Date) => {
    setPeriod((prev) => ({ ...prev, startDate: date }))
    setDownloadError(null)
  }, [])

  const setEndDate = useCallback((date: Date) => {
    setPeriod((prev) => ({ ...prev, endDate: date }))
    setDownloadError(null)
  }, [])

  const handleEncodingChange = useCallback((next: CsvEncodingType) => {
    setEncodingType(next)
    setDownloadError(null)
  }, [])

  const handleYesterdayClick = useCallback(() => setPeriod(getYesterdayPeriod()), [])
  const handleTodayClick = useCallback(() => setPeriod(getTodayPeriod()), [])
  const handleLastMonthClick = useCallback(() => setPeriod(getLastMonthPeriod()), [])
  const handleCurrentMonthClick = useCallback(() => setPeriod(getCurrentMonthPeriod()), [])

  const openConfirm = useCallback(() => {
    if (isDisabledDownload(period)) return
    setIsConfirmOpen(true)
  }, [period])

  const closeConfirm = useCallback(() => setIsConfirmOpen(false), [])

  const handleDownload = useCallback(() => {
    // 実行中の再入を防ぐ（確認モーダルの OK 連打で mutate が多重発火するのを止める）。
    if (isPending) return

    if (tenantId == null) {
      setIsConfirmOpen(false)
      setDownloadError('倉庫・テナントが選択されていません。')
      return
    }

    const { from, to } = toCreatedAtRange(period)
    // DL には時間がかかりうるため、押下直後にモーダルを閉じてボタンを無反応時間の
    // フィードバック（isDownloading による disabled）に切り替える。
    setIsConfirmOpen(false)
    setDownloadError(null)
    mutate(
      {
        tenantId,
        createdAtFrom: from,
        createdAtTo: to,
        encodingType,
        fallbackFilename: buildDefaultCsvFilename(period),
      },
      {
        onError: (error) => {
          // WHY: 詳細画面の添付 DL（副次操作）は 401/403 でも logout せず toast のみだが、
          // 当画面は CSV DL 自体が主目的のため、認証切れは再ログインへ倒す方が自然。
          if (isAuthDownloadError(error)) {
            logout()
            return
          }
          setDownloadError(getDownloadErrorMessage(error))
        },
      }
    )
  }, [isPending, tenantId, period, encodingType, mutate])

  return {
    period,
    encodingType,
    isOverPeriod: isOverDownloadPeriod(period),
    isBeforeStartDate: isBeforeStartDate(period),
    isDisabled: isDisabledDownload(period),
    isDownloading: isPending,
    isConfirmOpen,
    downloadError,
    setStartDate,
    setEndDate,
    setEncodingType: handleEncodingChange,
    handleYesterdayClick,
    handleTodayClick,
    handleLastMonthClick,
    handleCurrentMonthClick,
    openConfirm,
    closeConfirm,
    handleDownload,
  }
}
